/**
 * NotificationSettingsService - Firestore CRUD operations for user notification settings
 * 
 * Boundary: Does not validate business logic or enforce quiet hours; storage layer only
 * External dependencies: Firestore collection 'user-notification-settings'
 */

import { Injectable } from '@angular/core';
import { query, where, limit } from '@angular/fire/firestore';
import { FirestoreService } from '@core/services/firestore.abstract.service';
import { v4 } from 'uuid';
import {
  NotificationPreference,
  UserNotificationSettings,
  NotificationType,
  DEFAULT_NOTIFICATION_PREFERENCES,
} from '@core/models/notification.types';

/**
 * Manages Firestore CRUD operations for UserNotificationSettings with validation and default generation
 * 
 * Dependencies:
 * - FirestoreService<UserNotificationSettings>: Abstract Firestore base class
 * 
 * Side effects:
 * - Firestore reads/writes to 'user-notification-settings' collection
 * - Console error logging on failures
 * - Converts preferences from object to array format for backward compatibility
 */
@Injectable({
  providedIn: 'root',
})
export class NotificationSettingsService extends FirestoreService<UserNotificationSettings> {
  protected override basePath: string = 'user-notification-settings';

  /**
   * Retrieves notification settings for a specific user from Firestore
   * 
   * @param userId - User identifier
   * @returns Promise<UserNotificationSettings | null> user settings or null if not found
   * - Observable semantics: N/A (Promise-based async operation)
   * - HTTP errors: Logged to console; returns null on error
   * - State touched: Firestore query on 'user-notification-settings' collection
   * - Idempotency: Safe read-only operation
   * 
   * @example
   * // Automatically converts legacy object-based preferences to array format
   */
  async getUserNotificationSettings(
    userId: string,
  ): Promise<UserNotificationSettings | null> {
    try {
      const querySnapshot = await this.getCollection$((collectionRef: any) =>
        query(collectionRef, where('userId', '==', userId), limit(1)),
      );

      if (querySnapshot.docs.length > 0) {
        const settings =
          querySnapshot.docs[0].data() as UserNotificationSettings;
        // convert preferences from object to array if needed
        if (
          settings.preferences &&
          typeof settings.preferences === 'object' &&
          !Array.isArray(settings.preferences)
        ) {
          settings.preferences = Object.values(settings.preferences);
        }
        return settings;
      }
      return null;
    } catch (error) {
      console.error('Error fetching user notification settings:', error);
      return null;
    }
  }

  /**
   * Creates or updates user notification settings with validation
   * 
   * @param settings - Notification settings object to save or update
   * @returns Promise<string> UID of saved/updated settings record
   * - Observable semantics: N/A (Promise-based async operation)
   * - HTTP errors: Throws Error on validation failure or Firestore operation failure
   * - State touched: Firestore write to 'user-notification-settings' collection; creates or updates document
   * - Idempotency: Updates existing settings if found (upsert behavior); safe to call multiple times
   * 
   * @example
   * // Validates userId, email format, and optional phone format before saving
   */
  async saveUserNotificationSettings(
    settings: UserNotificationSettings,
  ): Promise<string> {
    // Validation
    if (!settings.userId) {
      throw new Error('User ID is required');
    }

    if (!settings.email || !this.isValidEmail(settings.email)) {
      throw new Error('Valid email address is required');
    }

    // Validate phone number if provided
    if (settings.smsNumber && !this.isValidPhoneNumber(settings.smsNumber)) {
      throw new Error('Invalid phone number format');
    }

    settings.updatedAt = new Date();

    // Check if user already has settings
    const existingSettings = await this.getUserNotificationSettings(
      settings.userId,
    );

    if (existingSettings && existingSettings.uid) {
      // Update existing settings
      const updatedSettings = { ...existingSettings, ...settings };
      updatedSettings.uid = existingSettings.uid;
      await this.update(updatedSettings, existingSettings.uid);
      return existingSettings.uid;
    } else {
      // Create new settings
      settings.createdAt = new Date();
      const result = await this.create(settings);
      return typeof result === 'object' ? result.uid : result;
    }
  }

  /**
   * Validate email format using regex pattern
   * @param email - The email address to validate
   * @returns boolean - True if email format is valid, false otherwise
   * @private
   */
  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Validate phone number format using regex pattern
   * @param phone - The phone number to validate
   * @returns boolean - True if phone format is valid or empty (optional), false otherwise
   * @private
   */
  private isValidPhoneNumber(phone: string): boolean {
    if (!phone.trim()) return true; // Empty phone is valid (optional field)
    const phoneRegex = /^[+]?[0-9\s\-()]{10,17}$/;
    return phoneRegex.test(phone);
  }

  /**
   * Generates default notification settings for a new user
   * 
   * @param userId - User identifier
   * @param email - User email address
   * @param smsNumber - Optional SMS number for the user
   * @returns Promise<UserNotificationSettings> default settings (or existing settings if found)
   * - Observable semantics: N/A (Promise-based async operation)
   * - HTTP errors: None (no Firestore write; read-only if existing settings found)
   * - State touched: Firestore read via getUserNotificationSettings; no writes
   * - Idempotency: Safe to call multiple times; returns existing settings if present
   * 
   * @example
   * // Default settings include all notification types enabled with standard quiet hours (22:00-08:00)
   */
  async createDefaultSettings(
    userId: string,
    email: string,
    smsNumber?: string,
  ): Promise<UserNotificationSettings> {
    const defaultSettings: UserNotificationSettings = {
      uid: v4(),
      userId,
      email,
      smsNumber: smsNumber || '',
      globalEnabled: true,
      weeklyReports: true,
      threatIntelUpdates: false,
      quietHours: {
        enabled: true,
        startTime: '22:00',
        endTime: '08:00',
      },
      timezone: 'UTC-5',
      weekendsOnly: false,
      preferences: DEFAULT_NOTIFICATION_PREFERENCES,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    // If user already has settings, return them
    const existingSettings = await this.getUserNotificationSettings(userId);
    if (existingSettings) {
      defaultSettings.uid = existingSettings.uid;
      defaultSettings.userId = existingSettings.userId;
      defaultSettings.email = existingSettings.email;

      // patch preferences from object to array
      if (
        existingSettings.preferences &&
        Array.isArray(existingSettings.preferences)
      ) {
        defaultSettings.preferences = existingSettings.preferences;
      } else if (
        existingSettings.preferences &&
        typeof existingSettings.preferences === 'object'
      ) {
        defaultSettings.preferences = Object.values(
          existingSettings.preferences,
        );
      }
    }

    return { ...defaultSettings };
  }

  /**
   * Updates a specific notification channel preference for a user
   * 
   * @param userId - User identifier
   * @param notificationType - Notification type to update (e.g., NotificationType.SECURITY_ALERTS)
   * @param channel - Channel to configure ('email', 'push', or 'sms')
   * @param enabled - true to enable, false to disable
   * @returns Promise<void>
   * - Observable semantics: N/A (Promise-based async operation)
   * - HTTP errors: None (no error handling; will throw on Firestore failure)
   * - State touched: Firestore update to specific preference channel; sets updatedAt timestamp
   * - Idempotency: Safe to call multiple times with same state; updates timestamp each time
   */
  async updateNotificationPreference(
    userId: string,
    notificationType: NotificationType,
    channel: 'email' | 'push' | 'sms',
    enabled: boolean,
  ): Promise<void> {
    const settings = await this.getUserNotificationSettings(userId);

    if (settings && settings.uid) {
      const preferenceIndex = settings.preferences.findIndex(
        (p: NotificationPreference) => p.type === notificationType,
      );

      if (preferenceIndex !== -1) {
        settings.preferences[preferenceIndex][channel] = enabled;
        settings.updatedAt = new Date();
        await this.update(settings, settings.uid);
      }
    }
  }

  /**
   * Enables or disables global notifications for a user
   * 
   * @param userId - User identifier
   * @param enabled - true to enable globally, false to disable
   * @returns Promise<void>
   * - Observable semantics: N/A (Promise-based async operation)
   * - HTTP errors: None (no error handling; will throw on Firestore failure)
   * - State touched: Firestore update to globalEnabled field; sets updatedAt timestamp
   * - Idempotency: Safe to call multiple times with same state; updates timestamp each time
   */
  async toggleGlobalNotifications(
    userId: string,
    enabled: boolean,
  ): Promise<void> {
    const settings = await this.getUserNotificationSettings(userId);

    if (settings && settings.uid) {
      settings.globalEnabled = enabled;
      settings.updatedAt = new Date();
      await this.update(settings, settings.uid);
    }
  }

  /**
   * Disables all notification channels except mandatory security alerts
   * 
   * @param userId - User identifier
   * @returns Promise<void>
   * - Observable semantics: N/A (Promise-based async operation)
   * - HTTP errors: None (no error handling; will throw on Firestore failure)
   * - State touched: Firestore update to all preferences (email, push, sms set to false except SECURITY_ALERTS); sets updatedAt
   * - Idempotency: Safe to call multiple times; security alerts always preserved
   * 
   * @example
   * // Security alerts remain enabled for safety purposes
   */
  async unsubscribeFromAll(userId: string): Promise<void> {
    const settings = await this.getUserNotificationSettings(userId);

    if (settings && settings.uid) {
      settings.preferences.forEach((pref: NotificationPreference) => {
        if (pref.type !== NotificationType.SECURITY_ALERTS) {
          pref.email = false;
          pref.push = false;
          pref.sms = false;
        }
      });
      settings.updatedAt = new Date();
      await this.update(settings, settings.uid);
    }
  }
}
